import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiNavigation, FiMessageCircle } = FiIcons;

function Contact() {
  const contactInfo = [
    {
      icon: FiPhone,
      title: "Phone",
      details: "076-8727112",
      action: "tel:076-8727112",
      actionText: "Call Now"
    },
    {
      icon: FiMail,
      title: "Email",
      details: "info@kumarana-panamure.com",
      action: "mailto:info@kumarana-panamure.com",
      actionText: "Send Email"
    },
    {
      icon: FiMapPin,
      title: "Location",
      details: "Panamure Junction, Main Road, Kolonna",
      action: "https://maps.google.com/?q=Kumarana+Hotel+Panamure",
      actionText: "Get Directions"
    },
    {
      icon: FiClock,
      title: "Hours",
      details: "24/7 Reception | Restaurant: 6:00 AM - 10:00 PM",
      action: null,
      actionText: null
    }
  ];

  return (
    <section id="contact" className="section-padding">
      <div className="container">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get in <span className="text-gradient">Touch</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Ready to experience the best of Sabaragamuwa hospitality? Contact us for reservations, 
            group bookings, or any inquiries about our services and nearby attractions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="glass-card p-6"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 glass-card rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={info.icon} className="text-blue-300" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-semibold mb-2">{info.title}</h4>
                    <p className="text-white/80 mb-4">{info.details}</p>
                    {info.action && (
                      <a 
                        href={info.action}
                        target={info.action.startsWith('http') ? '_blank' : undefined}
                        rel={info.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="btn-glass text-sm"
                      >
                        {info.actionText}
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="glass-card p-8">
              <h3 className="text-2xl font-bold mb-6">Quick Actions</h3>
              <div className="space-y-4">
                <a 
                  href="tel:076-8727112"
                  className="btn-glass w-full justify-center pulse-glow"
                >
                  <SafeIcon icon={FiPhone} />
                  Call for Immediate Booking
                </a>
                <a 
                  href="https://wa.me/94768727112?text=Hi, I'd like to make a reservation at Kumarana Hotel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-glass w-full justify-center"
                >
                  <SafeIcon icon={FiMessageCircle} />
                  WhatsApp Booking
                </a>
                <a 
                  href="https://maps.google.com/?q=Kumarana+Hotel+Panamure"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-glass w-full justify-center"
                >
                  <SafeIcon icon={FiNavigation} />
                  Get Directions
                </a>
              </div>
            </div>

            <div className="glass-card p-8">
              <h4 className="text-xl font-semibold mb-4">SLTDA Registration</h4>
              <div className="space-y-3 text-white/80">
                <div className="flex justify-between">
                  <span>Registration Number:</span>
                  <span className="text-blue-300 font-mono">SLTDA/SQA/BUN/01295</span>
                </div>
                <div className="flex justify-between">
                  <span>License Number:</span>
                  <span className="text-blue-300 font-mono">BUN/2025/0075</span>
                </div>
                <div className="flex justify-between">
                  <span>Classification:</span>
                  <span className="text-green-400">Superior Bungalow</span>
                </div>
                <div className="flex justify-between">
                  <span>Valid Until:</span>
                  <span className="text-white">December 2025</span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h4 className="text-lg font-semibold mb-4">Location Highlights</h4>
              <ul className="space-y-2 text-white/80">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Strategic junction location on Main Road</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span>15 minutes to Maduwanwela Walawwa</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Gateway to Kolonna Electorate</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Perfect lunch stop for tour groups</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default Contact;